/**
 * What one brand-colour CHOICE is made of before any surface decides how to draw it: the solid, the
 * fill a label sits on, the gradient stops and the label's ink.
 *
 * ── Why this is in `shared/` ─────────────────────────────────────────────────────────────────
 * Two painters read one choice and must agree: the trainer's signed-in app (`entities/brand-color`)
 * and the public form's Studio template (`entities/public-form`), whose „Jak w aplikacji" is
 * promised to paint as the app does. An entity may not import another, and the public form had
 * grown a second reading of a custom colour (a three-stop sweep, with its own ink choice) that the
 * app never drew. The rule moved down a layer on 27 September 2026 so there is one.
 *
 * A preset is its handoff table, verbatim. A custom colour is FLAT, as on the phone, and its fill is
 * moved until a white label clears AA, as `accentCta` does there.
 *
 * The public form reads presets from here but NOT a custom colour: on 27 September 2026 the owner
 * decided the form paints a custom colour exactly as typed and picks its ink instead
 * (`entities/public-form/model/theme.ts`). The two rules differ on purpose.
 */

import { AA_TEXT_RATIO, readableFill } from '~/shared/lib/contrast';

import { BRAND_PRESETS, brandPresetById, type BrandPresetId } from './brandPresets';

/** A trainer's brand colour: one of the presets, or a `#rrggbb` of their own. */
export type BrandChoice =
  | { readonly kind: 'preset'; readonly preset: BrandPresetId }
  | { readonly kind: 'custom'; readonly hex: string };

/** White — the label every preset but Lime writes — and the near-black the bright ones take. */
export const BRAND_LIGHT_INK = '#ffffff';
export const BRAND_DARK_INK = '#111113';

export interface BrandHues {
  /** The hue the trainer chose. Words and shades start walking from it. */
  readonly solid: string;
  /** What a filled accent surface paints: a preset's solid, a custom hex moved under white. */
  readonly fill: string;
  readonly stops: readonly [string, string, string];
  readonly locations: readonly [number, number, number];
  /** The label on those fills: a preset's authored ink, white for a custom colour. */
  readonly label: 'light' | 'dark';
  readonly custom: boolean;
}

export function brandHues(brand: BrandChoice): BrandHues {
  if (brand.kind === 'custom') {
    const fill = readableFill(brand.hex, BRAND_LIGHT_INK, AA_TEXT_RATIO);
    return {
      solid: brand.hex,
      fill,
      stops: [fill, fill, fill],
      locations: [0, 0.5, 1],
      label: 'light',
      custom: true,
    };
  }
  const fallback = BRAND_PRESETS[0];
  if (fallback === undefined) throw new Error('the brand preset table is empty');
  const preset = brandPresetById(brand.preset) ?? fallback;
  return {
    solid: preset.solid,
    fill: preset.solid,
    stops: preset.stops,
    locations: preset.locations,
    label: preset.onTile,
    custom: false,
  };
}
